import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Agent, ChatMessage, FeedEvent, Toast, UserInfo } from './types';
import { FLOWS, LANG_NAMES } from './constants';
import { useSignalR, HUB_ENDPOINT, INTERNAL_SECRET } from './hooks/useSignalR';
import { useToasts }         from './hooks/useToasts';
import { useFlowRecorder }   from './hooks/useFlowRecorder';
import type { SequenceSession } from './components/guidance/SequenceLibrary';
import { Topbar }            from './components/layout/Topbar';
import { AppLogin }          from './components/layout/AppLogin';
import { signOut as cognitoSignOut } from './services/cognitoAuth';
import { ApiKeyModal }       from './components/layout/ApiKeyModal';
import { UserList }          from './components/users/UserList';
import { ActivityFeed }      from './components/activity/ActivityFeed';
import { GuidancePanel }     from './components/guidance/GuidancePanel';
// FIX 1: import as ChatPanelV2 (the actual export name) and alias it
import { ChatPanelV2 as ChatPanel } from './components/chat/ChatPanel';
import { UiTreeViewer }      from './components/uitree/UiTreeViewer';
import { ToastDock }         from './components/common/ToastDock';
import { CandidatesPanel }   from './components/candidates/CandidatesPanel';
import { SaasAdmin }         from './components/saas-admin/SaasAdmin';
import { AppSelector }       from './components/layout/AppSelector';

const uid = () => Math.random().toString(36).slice(2, 10);

type MainTab = 'activity' | 'guidance' | 'uitree' | 'candidates';
// Which top-level console the signed-in agent is viewing.
//   'select'  → the two-card chooser shown right after login
//   'saas'    → the SaaS Admin console
//   'support' → the Support Console (live activity / guidance / chat)
type AppView = 'select' | 'saas' | 'support';

export function App() {
  const [agent,        setAgent]        = useState<Agent | null>(null);
  const [apiKey,       setApiKey]       = useState('');
  const [showKeyModal, setShowKeyModal] = useState(false);
  const [selectedId,   setSelectedId]   = useState<string | null>(null);
  const [mainTab,      setMainTab]      = useState<MainTab>('activity');
  const [sequences,    setSequences]    = useState<SequenceSession[]>([]);
  const [appView,      setAppView]      = useState<AppView>('select');

  const recorder = useFlowRecorder();
  const { toasts, addToast, dismiss } = useToasts();

  // FIX 2: pass apiKey as second argument — useSignalR(agent, apiKey)
  const {
    status, users, events, chats, unread, uiElements,
    clearUnread, pushChat, pushEvent,
    sendAgentChat, sendFlow, sendHighlight,
    sendSequenceToElement,
  } = useSignalR(agent, apiKey);

  // Alert toasts
  const prevAlertCount = useRef(0);
  useEffect(() => {
    const alerts = events.filter(e =>
      e.type === 'stuck_alert' || e.type === 'idle_alert' ||
      (e.type === 'tracked_event' && e.needsAttention)
    );
    if (alerts.length > prevAlertCount.current) {
      const newest = alerts[0];
      if (newest) {
        const isIdle  = newest.type === 'idle_alert';
        const evLabel = newest.eventType ? ` [${newest.eventType}]` : '';
        addToast({
          title: isIdle
            ? `⏱ Idle: ${newest.userName}`
            : newest.type === 'tracked_event'
              ? `📡 ${newest.userName}${evLabel}`
              : `⚠ Stuck: ${newest.userName}`,
          msg:   newest.detail,
          icon:  isIdle ? '⏱' : newest.type === 'tracked_event' ? '📡' : '⚠️',
          color: isIdle ? 'amber' : newest.severity === 'error' ? 'red' : 'amber',
        });
      }
    }
    prevAlertCount.current = alerts.length;
  }, [events, addToast]);

  useEffect(() => { if (selectedId) clearUnread(selectedId); }, [selectedId, chats, clearUnread]);

  const selectedUser  = useMemo(() => users.find(u => u.id === selectedId) ?? null, [users, selectedId]);
  const selectedChats = useMemo(() => selectedId ? (chats[selectedId] ?? []) : [], [chats, selectedId]);
  const visibleEvents = useMemo(() => selectedId ? events.filter(e => e.userId === selectedId) : events, [events, selectedId]);
  const alertCount    = useMemo(() => users.filter(u => u.hasAlert).length, [users]);
  const trackedCount  = useMemo(() => events.filter(e => e.type === 'tracked_event' && e.needsAttention).length, [events]);

  const handleLogin   = useCallback((a: Agent, key: string) => { setAgent(a); setApiKey(key); setAppView('select'); }, []);
  const handleSignOut = useCallback(() => { cognitoSignOut(); setAgent(null); setAppView('select'); }, []);
  const handleSaveKey = useCallback((k: string) => { setApiKey(k); setShowKeyModal(false); }, []);

  const handleSendChat = useCallback(async (
    user: UserInfo, text: string, translated: string, usedLLM: boolean,
  ) => {
    if (!user) return;
    // Forward the console-side translation so the server can deliver it
    // verbatim to the user instead of falling back to its tiny static dictionary.
    await sendAgentChat(user.id, text, translated);
    recorder.recordText(text);
    pushChat(user.id, {
      id: uid(), dir: 'out', from: agent?.name ?? 'Agent',
      text, translated, usedLLM, ts: new Date(),
    } as ChatMessage);
    pushEvent({
      id: uid(), type: 'agent_sent', userId: user.id, userName: user.name,
      detail: `[${agent?.name}] ${text}`, ts: new Date(),
    } as FeedEvent);
    addToast({
      title: '✅ Message sent',
      msg:   `→ ${user.name} (${LANG_NAMES[user.lang] ?? user.lang})`,
      icon:  '✉️', color: 'green',
    });
  }, [sendAgentChat, recorder, pushChat, pushEvent, addToast, agent]);

  const handleSendFlow = useCallback((userId: string, flowId: string) => {
    sendFlow(userId, flowId);
    const u = users.find(x => x.id === userId);
    const f = FLOWS.find(x => x.id === flowId);
    pushChat(userId, { id: uid(), dir: 'sys', from: 'System', text: `📋 Flow sent: "${f?.title}"`, ts: new Date() } as ChatMessage);
    pushEvent({ id: uid(), type: 'flow_sent', userId, userName: u?.name ?? userId, detail: flowId, ts: new Date() } as FeedEvent);
  }, [sendFlow, users, pushChat, pushEvent]);

  const handleSendHighlight = useCallback((userId: string, seq: string[], ms: number) => {
    sendHighlight(userId, seq, ms);
    recorder.recordHighlight(seq, ms, uiElements, 'redPulse');
    const u = users.find(x => x.id === userId);
    pushChat(userId, {
      id: uid(), dir: 'sys', from: 'System',
      text: `✨ Highlighted: ${seq.slice(0, 4).join(' → ')}${seq.length > 4 ? '…' : ''}`,
      ts: new Date(),
    } as ChatMessage);
    pushEvent({
      id: uid(), type: 'highlight_sent', userId, userName: u?.name ?? userId,
      detail: `${seq.length} elements highlighted`, ts: new Date(),
    } as FeedEvent);
  }, [sendHighlight, recorder, uiElements, users, pushChat, pushEvent]);

  /**
   * Send a guided sequence that walks the customer from a known landing
   * point (side menu / top bar) down to the target element. The server's
   * TreeSequenceBuilder computes the ancestor path and pushes a multi-
   * step GuidanceFlow with per-step highlight + voice + chat.
   *
   * Triggered from the UI Tree viewer's "Send sequence to this element"
   * button when the agent has a user selected.
   */
  const handleSendSequence = useCallback((targetElementId: string, label: string) => {
    if (!selectedUser) return;
    sendSequenceToElement(selectedUser.id, targetElementId);
    pushChat(selectedUser.id, {
      id: uid(), dir: 'sys', from: 'System',
      text: `🧭 Sequence sent → ${label}`,
      ts: new Date(),
    } as ChatMessage);
    pushEvent({
      id: uid(), type: 'sequence_sent', userId: selectedUser.id,
      userName: selectedUser.name, detail: `→ ${label}`, ts: new Date(),
    } as FeedEvent);
  }, [selectedUser, sendSequenceToElement, pushChat, pushEvent]);

  const [pendingElementId, setPendingElementId] = useState<string | null>(null);
  const handleTreeElementSelect = useCallback((id: string) => {
    setPendingElementId(id);
    setMainTab('guidance');
  }, []);

  const handleSaveRecording = useCallback((seq: SequenceSession) => {
    setSequences(p => [seq, ...p]);
  }, []);

  if (!agent) {
    return (
      <>
        <AppLogin onLogin={handleLogin}/>
        <ToastDock toasts={toasts} onDismiss={dismiss}/>
      </>
    );
  }

  // After login, show the two-console chooser until the agent picks one.
  if (appView === 'select') {
    return (
      <>
        <AppSelector agent={agent} onSelect={setAppView} onSignOut={handleSignOut}/>
        <ToastDock toasts={toasts} onDismiss={dismiss}/>
      </>
    );
  }

  // SaaS Admin console — "Back" returns to the chooser.
  if (appView === 'saas') {
    return (
      <>
        <SaasAdmin onExit={() => setAppView('select')}/>
        <ToastDock toasts={toasts} onDismiss={dismiss}/>
      </>
    );
  }

  const TABS: { id: MainTab; icon: string; label: string; badge?: number }[] = [
    { id: 'activity',   icon: '📊', label: 'Activity', badge: Math.min(events.length, 99) },
    { id: 'guidance',   icon: '🧭', label: 'Guidance' },
    { id: 'uitree',     icon: '🌳', label: 'UI Tree',  badge: uiElements.length },
    { id: 'candidates', icon: '🔭', label: 'Candidates' },
  ];

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'var(--sidebar-w) 1fr var(--chat-w)',
      gridTemplateRows: 'var(--topbar-h) 1fr',
      height: '100vh', width: '100vw',
    }}>

      <Topbar agent={agent} users={users} status={status} apiKey={apiKey}
        onOpenApiKey={() => setShowKeyModal(true)} onSignOut={handleSignOut}
        onSwitchApp={() => setAppView('select')}/>

      <UserList users={users} selectedId={selectedId} unread={unread} agent={agent} onSelect={setSelectedId}/>

      {/* Center panel */}
      <div style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', background: 'var(--bg-void)', position: 'relative' }}>
        <div style={{ position: 'absolute', inset: 0, backgroundImage: 'linear-gradient(rgba(245,166,35,0.025) 1px,transparent 1px),linear-gradient(90deg,rgba(245,166,35,0.025) 1px,transparent 1px)', backgroundSize: '32px 32px', pointerEvents: 'none' }}/>

        {/* Header */}
        <div style={{ position: 'relative', zIndex: 1, background: 'var(--bg-base)', borderBottom: '1px solid var(--br-base)', padding: '10px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <div>
            <div style={{ fontFamily: 'var(--f-display)', fontSize: 14, fontWeight: 700 }}>
              {selectedUser ? selectedUser.name : 'All Activity'}
            </div>
            <div style={{ fontFamily: 'var(--f-mono)', fontSize: 10, color: 'var(--tx-muted)', marginTop: 2, display: 'flex', gap: 8 }}>
              {selectedUser
                ? <><span>📍 {selectedUser.currentPage ?? 'Unknown'}</span><span>·</span><span>{selectedUser.totalClicks} clicks</span></>
                : <><span>{events.length} events</span><span>·</span><span>{users.filter(u => u.isOnline).length} online</span>
                    {alertCount   > 0 && <span style={{ color: '#F5A623' }}>· ⚠ {alertCount} alerts</span>}
                    {trackedCount > 0 && <span style={{ color: '#F43F5E' }}>· 📡 {trackedCount} needs attention</span>}
                  </>
              }
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {recorder.state.isRecording && (
              <span style={{ fontSize: 10, fontWeight: 700, color: '#C62828', display: 'flex', alignItems: 'center', gap: 4 }}>
                <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#E53935', display: 'inline-block' }}/>
                REC {recorder.state.steps.length}
              </span>
            )}
            {selectedUser && (
              <button onClick={() => setSelectedId(null)}
                style={{ padding: '5px 11px', fontSize: 11, fontWeight: 600, background: 'transparent', border: '1px solid var(--br-base)', borderRadius: 'var(--r)', color: 'var(--tx-secondary)', cursor: 'pointer' }}>
                ✕ Deselect
              </button>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div style={{ position: 'relative', zIndex: 1, display: 'flex', background: 'var(--bg-base)', borderBottom: '1px solid var(--br-base)', padding: '0 16px', flexShrink: 0 }}>
          {TABS.map(tab => (
            <button key={tab.id} onClick={() => setMainTab(tab.id)}
              style={{
                padding: '9px 12px', fontFamily: 'var(--f-display)', fontSize: 11, fontWeight: 600,
                color: mainTab === tab.id ? 'var(--amber)' : 'var(--tx-muted)',
                background: 'transparent', border: 'none',
                borderBottom: mainTab === tab.id ? '2px solid var(--amber)' : '2px solid transparent',
                cursor: 'pointer', transition: 'all 0.14s', display: 'flex', alignItems: 'center', gap: 6,
              }}>
              {tab.icon} {tab.label}
              {tab.badge != null && tab.badge > 0 && (
                <span style={{ padding: '1px 5px', background: mainTab === tab.id ? 'var(--amber-glow)' : 'var(--bg-raised)', borderRadius: 8, fontSize: 9, fontFamily: 'var(--f-mono)', color: mainTab === tab.id ? 'var(--amber)' : 'var(--tx-muted)' }}>
                  {tab.badge}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Tab content */}
        <div style={{ position: 'relative', zIndex: 1, flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          {mainTab === 'activity' && <ActivityFeed events={visibleEvents} selectedUserName={selectedUser?.name}/>}

          {mainTab === 'guidance' && (
            <GuidancePanel
              user={selectedUser}
              uiElements={uiElements}
              onSendFlow={handleSendFlow}
              onSendHighlight={handleSendHighlight}
              onSendSequence={handleSendSequence}
              addToast={addToast as (t: Omit<Toast, 'id'>) => void}
              pendingElementId={pendingElementId}
              onPendingElementConsumed={() => setPendingElementId(null)}
              recorder={recorder}
              onSaveRecording={handleSaveRecording}
            />
          )}

          {mainTab === 'uitree' && (
            <UiTreeViewer
              elements={uiElements}
              onSelectElement={handleTreeElementSelect}
              onHighlight={(id, _label) => {
                if (!selectedUser) {
                  addToast({
                    title: 'Select a user first',
                    msg:   'Pick a user from the left panel, then highlight an element.',
                    icon:  '⚠️',
                    color: 'amber',
                  });
                  return;
                }
                // Single-element highlight: pass [id] as a one-step sequence.
                handleSendHighlight(selectedUser.id, [id], 1800);
              }}
              onSendSequence={(id, label) => {
                if (!selectedUser) {
                  addToast({
                    title: 'Select a user first',
                    msg:   'Pick a user from the left panel before sending a guided sequence.',
                    icon:  '⚠️',
                    color: 'amber',
                  });
                  return;
                }
                handleSendSequence(id, label);
              }}
            />
          )}

          {mainTab === 'candidates' && (
            <CandidatesPanel
              apiBaseUrl={HUB_ENDPOINT.replace(/\/hubs\/.*$/, '')}
              internalSecret={INTERNAL_SECRET}
              active={mainTab === 'candidates'}
            />
          )}
        </div>
      </div>

      {/* Right chat panel — FIX 3: ChatPanel is now ChatPanelV2 aliased above */}
      <ChatPanel user={selectedUser} messages={selectedChats} agent={agent}
        apiKey={apiKey} onSend={handleSendChat}/>

      <ToastDock toasts={toasts} onDismiss={dismiss}/>

      {showKeyModal && (
        <ApiKeyModal currentKey={apiKey} onSave={handleSaveKey} onClose={() => setShowKeyModal(false)}/>
      )}
    </div>
  );
}
